import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  Users, Radio, BookOpen, Video, GraduationCap, Sparkles, ArrowRight, LayoutDashboard,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const safeLen = (p) => p.then((r) => (Array.isArray(r.data) ? r.data.length : (r.data?.clients?.length ?? 0))).catch(() => null);

export default function AdminMemberPortal() {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const opt = { withCredentials: true };
    Promise.all([
      safeLen(axios.get(`${API_URL}/api/admin/clients`, opt)),
      safeLen(axios.get(`${API_URL}/api/admin/group-sessions`, opt)),
      safeLen(axios.get(`${API_URL}/api/admin/webinars`, opt)),
      safeLen(axios.get(`${API_URL}/api/admin/webinar-schedules`, opt)),
      safeLen(axios.get(`${API_URL}/api/resources`)),
    ]).then(([clients, groups, webinars, schedules, resources]) =>
      setCounts({ clients, groups, webinars, schedules, resources }));
  }, []);

  const cards = [
    {
      key: 'members', icon: Users, to: '/admin/clients',
      title: 'Members & Access', count: counts.clients,
      desc: 'View members, manage onboarding, 1-on-1 sessions, AI recaps, tasks and portal access.',
    },
    {
      key: 'training', icon: Radio, to: '/admin/webinars',
      title: 'Live Training & Dates', count: counts.webinars,
      desc: 'Create trainings, set dates/times, add Zoom or stream links, manage recurring Monday workshops, replays & registrations.',
      sub: counts.schedules != null ? `${counts.schedules} recurring schedule${counts.schedules === 1 ? '' : 's'}` : null,
    },
    {
      key: 'group', icon: Video, to: '/admin/group-sessions',
      title: 'Group Sessions', count: counts.groups,
      desc: 'Host member-wide group calls. Add recordings, transcripts and AI-generated recaps visible to every member.',
    },
    {
      key: 'resources', icon: BookOpen, to: '/admin/resources',
      title: 'Member Resources', count: counts.resources,
      desc: 'Publish guides, checklists and downloads to the member Resource Library — available to everyone.',
    },
    {
      key: 'programs', icon: GraduationCap, to: '/admin/services',
      title: 'Programs & Services', count: null,
      desc: 'Manage the coaching programs and services members see under Programs and at checkout.',
    },
    {
      key: 'studio', icon: Sparkles, to: '/admin/social-studio',
      title: 'Social Studio', count: null,
      desc: 'Manage the AI Social Studio: brand profiles, theme banks, content plans and member studio access.',
    },
  ];

  return (
    <AdminLayout title="Member Portal">
      <div className="space-y-6">
        <div>
          <h1 className="heading-2 text-2xl text-[#0A0F1C] flex items-center gap-2" data-testid="member-portal-hub-title">
            <LayoutDashboard size={22} className="text-[#1D4ED8]" /> Member Portal Control Center
          </h1>
          <p className="text-sm text-[#475569]">One place to control everything members see and do — training, group sessions, resources, sessions and access.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="member-portal-hub-grid">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.key} to={c.to} data-testid={`hub-card-${c.key}`}
                className="group bg-white border border-[#E2E8F0] rounded-2xl p-6 flex flex-col hover:-translate-y-0.5 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <span className="w-11 h-11 rounded-xl bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center"><Icon size={20} /></span>
                  {c.count != null && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#475569]" data-testid={`hub-count-${c.key}`}>{c.count}</span>
                  )}
                </div>
                <h3 className="font-semibold text-[#0A0F1C] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.title}</h3>
                {c.sub && <span className="text-xs text-[#F97316] font-medium">{c.sub}</span>}
                <p className="text-sm text-[#475569] mt-2 flex-grow">{c.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1D4ED8] group-hover:gap-2.5 transition-all">Manage <ArrowRight size={15} /></span>
              </Link>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
